import React from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import ReportCreateNewLabelField from '../ReportCreateNewLabelField';
import { TextField } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import AutocompleteWithCheckBoxes from '../Autocomplete';
import { Autocomplete } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { ICohortDetails } from '../../organisms/CreateNewModalCoreComponent';
import { useStores } from '../../../stores';
import { toJS } from 'mobx';

interface CohortPropertyFieldProps {
  cohort: ICohortDetails;
  index: number;
  loadingPropertiesByIndex: Record<number, boolean>;
  onSingleChange: (_index: number, _value: any) => void;
  onMultipleChange: (_index: number, _value: any) => void;
  onTextChange: (_index: number, _value: any) => void;
  onScrollEvent: (_cohort: ICohortDetails) => void;
  onSearchChange: (
    _e: React.ChangeEvent<HTMLInputElement>,
    _cohort: ICohortDetails,
    _index: number,
  ) => void;
}

const listboxStyle: React.CSSProperties = {
  padding: '0px',
  boxSizing: 'border-box',
  border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  backgroundColor: patientAccessTheme.palette.status.white,
  margin: '0px',
  maxHeight: '280px',
};

const useStyles = makeStyles()({
  autoCompleteDropdown: {
    '&  > .MuiInputBase-root': {
      height: '36px',
      width: '301px',
      border: 'none',
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: '4px 0px 4px 8px',
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
      padding: '0px',
      paddingLeft: '4px',
    },
    '&. MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input': {
      padding: '0px',
      paddingLeft: '4px',
    },
  },
  autoCompleteInput: {
    border: 'none',
    backgroundColor: patientAccessTheme.palette.status.white,
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    '& input': {
      fontSize: '14px',
      lineHeight: '24px',
      color: patientAccessTheme.palette.text.mediumemphasis,
      fontWeight: 500,
    },
    '& input::placeholder': {
      fontSize: '14px',
      lineHeight: '24px',
      color: patientAccessTheme.palette.text.mediumemphasis,
      fontWeight: 500,
    },
    '& .MuiAutocomplete-input': {
      padding: '0px',
      height: '27px',
    },
    '&. MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input': {
      padding: '0px',
    },
  },
  paper: {
    boxShadow: 'none',
    margin: '0px',
  },
  tag: {
    margin: '3px 8px',
  },
});

export const CohortPropertyField = ({
  cohort,
  index,
  loadingPropertiesByIndex,
  onSingleChange,
  onMultipleChange,
  onTextChange,
  onScrollEvent,
  onSearchChange,
}: CohortPropertyFieldProps) => {
  const { classes } = useStyles();
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const constants = componentConstants.ATOMS.COHORT_DETAILS;

  const propertyDropDownOptions = toJS(
    cohortDetailsStore.propertyDropDownOptions,
  );
  const cohortSelectionsValues = toJS(cohortDetailsStore.cohortSelectionsTypes);

  const options =
    propertyDropDownOptions?.[cohort.dropdownSelectionTypeIndex] ?? [];

  const isNumerical = options[0] === 'numerical value';
  const isSingle =
    cohortSelectionsValues[cohort.dropdownSelectionTypeIndex] === 'single';
  const isLoading = !!loadingPropertiesByIndex[index];

  const sharedScrollProps = {
    onScroll: (event: React.SyntheticEvent) => {
      const node = event.currentTarget;
      if (node.scrollTop + node.clientHeight === node.scrollHeight) {
        onScrollEvent(cohort);
      }
    },
    style: listboxStyle,
  };

  if (isNumerical) {
    return (
      <ReportCreateNewLabelField
        label={constants.PROPERTY}
        component={
          <TextField
            value={cohort.property}
            placeholder={constants.TEXT_FIELD_PLACEHOLDER}
            height="36px"
            width="301px"
            inputPropsStyle={{ paddingLeft: '16px' }}
            onChange={(e: any) => onTextChange(index, e.target.value)}
          />
        }
      />
    );
  }

  if (isSingle) {
    return (
      <ReportCreateNewLabelField
        label={constants.PROPERTY}
        component={
          <Autocomplete
            style={{ width: '226px' }}
            options={options}
            getOptionLabel={(o) => o.toString()}
            noOptionsText="No Field found"
            value={cohort.property}
            loading={isLoading}
            loadingText="Loading Properties..."
            onChange={(_, value) => onSingleChange(index, value)}
            slotProps={{ listbox: sharedScrollProps }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select"
                onChange={(e: any) => onSearchChange(e, cohort, index)}
              />
            )}
            popupIcon={
              isLoading ? <CircularProgress size={25} /> : <ArrowDropDownIcon />
            }
            classes={{
              root: classes.autoCompleteDropdown,
              input: classes.autoCompleteInput,
              inputRoot: classes.autoCompleteInput,
              paper: classes.paper,
              tag: classes.tag,
            }}
          />
        }
      />
    );
  }

  return (
    <ReportCreateNewLabelField
      label={constants.PROPERTY}
      component={
        <AutocompleteWithCheckBoxes
          width="226px"
          options={options}
          getOptionLabel={(o) => o.toString()}
          handleChange={(_, value) => onMultipleChange(index, value)}
          defaultValue={cohort.multipleProperties ?? []}
          placeholder={cohort.multipleProperties?.length === 0 ? 'Select' : ''}
          onScrollEvent={(e: React.SyntheticEvent) => {
            const node = e.currentTarget;
            if (node.scrollTop + node.clientHeight === node.scrollHeight) {
              onScrollEvent(cohort);
            }
          }}
          popupIcon={
            isLoading ? <CircularProgress size={25} /> : <ArrowDropDownIcon />
          }
        />
      }
    />
  );
};
